import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Target, ArrowRight, RefreshCw, AlertTriangle, CheckCircle2, Loader2, Film } from "lucide-react";

function StatusPill({ row }) {
  const s = row.last_status;
  if (s === "running" || s === "queued") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-amber-400 font-mono-tc uppercase">
        <Loader2 size={11} className="animate-spin" />
        {row.last_stage || s} · {row.last_progress || 0}%
      </span>
    );
  }
  if (s === "failed") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-rose-400 font-mono-tc uppercase">
        <AlertTriangle size={11} /> Failed
      </span>
    );
  }
  if (s === "done") {
    if (row.needs_render) {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-amber-400 font-mono-tc uppercase">
          <AlertTriangle size={11} /> Cuts ready · not rendered
        </span>
      );
    }
    if ((row.cuts_created || 0) === 0) {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-stone-500 font-mono-tc uppercase">
          0 cuts found
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400 font-mono-tc uppercase">
        <CheckCircle2 size={11} /> Rendered
      </span>
    );
  }
  return <span className="text-xs text-stone-500 font-mono-tc uppercase">{s || "—"}</span>;
}

export default function TrackedRiders() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    try {
      const r = await api.get("/tracked-riders");
      setRows(r.data.tracked_riders || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Auto-refresh while any job is running
    const id = setInterval(() => {
      setRows((prev) => {
        if (prev.some((r) => r.last_status === "running" || r.last_status === "queued")) load();
        return prev;
      });
    }, 4000);
    return () => clearInterval(id);
  }, []);

  const filtered = q
    ? rows.filter((r) => r.name.toLowerCase().includes(q.toLowerCase()))
    : rows;

  const counts = {
    total: rows.length,
    running: rows.filter((r) => r.last_status === "running" || r.last_status === "queued").length,
    needs_render: rows.filter((r) => r.needs_render).length,
    rendered: rows.filter((r) => r.last_status === "done" && r.clips_rendered > 0).length,
  };

  return (
    <div className="px-6 md:px-12 py-10 max-w-[1300px]" data-testid="tracked-riders-page">
      <div className="border-l-4 border-orange-600 pl-5 mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-tiny mb-2">AI Visual Tracking</div>
          <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">Tracked Riders</h1>
          <p className="text-stone-400 mt-3 max-w-xl">
            Every rider you've asked the AI to find via Track Rider. See job status, jump back into Studio to render pending cuts, or open the rider's profile once their clips exist.
          </p>
        </div>
        <button
          onClick={load}
          className="btn-secondary inline-flex items-center gap-2"
          data-testid="tracked-refresh-btn"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="tracked-stats">
        <Stat label="Total Riders" value={counts.total} />
        <Stat label="In Progress" value={counts.running} accent="amber" />
        <Stat label="Cuts Awaiting Render" value={counts.needs_render} accent="amber" />
        <Stat label="Rendered + Live" value={counts.rendered} accent="emerald" />
      </div>

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Filter by name…"
        className="field-input !w-full !max-w-md mb-6"
        data-testid="tracked-filter"
      />

      {loading ? (
        <div className="text-stone-500 py-12 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-stone-800 p-16 text-center" data-testid="tracked-empty">
          <Target size={32} className="mx-auto text-stone-600 mb-3" />
          <p className="text-stone-400">No tracked riders yet.</p>
          <p className="text-stone-500 text-sm mt-2">
            Open a video in <Link to="/studio" className="text-orange-500 underline">Studio</Link>, click <span className="font-mono-tc">Track Rider</span>, draw a box around the rider you want and run the AI.
          </p>
        </div>
      ) : (
        <div className="border border-stone-800 divide-y divide-stone-800" data-testid="tracked-list">
          {filtered.map((r) => (
            <div
              key={r.name}
              className="p-4 md:p-5 hover:bg-stone-900/40 transition-colors flex flex-col md:flex-row md:items-center gap-4"
              data-testid={`tracked-row-${r.name}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                  <div className="font-display uppercase font-bold text-2xl truncate">{r.name}</div>
                  <StatusPill row={r} />
                  {r.had_fallback && (
                    <span className="text-[10px] font-mono-tc uppercase text-amber-400/80 border border-amber-700/40 px-1.5 py-0.5">
                      Single-pass fallback
                    </span>
                  )}
                </div>
                <div className="text-xs text-stone-500 font-mono-tc flex flex-wrap gap-x-4 gap-y-1">
                  <span>
                    <span className="text-orange-500">{r.cuts_created || 0}</span> cut{r.cuts_created !== 1 ? "s" : ""} found
                  </span>
                  <span>
                    <span className="text-stone-300">{r.clips_rendered || 0}</span> clip{r.clips_rendered !== 1 ? "s" : ""} rendered
                  </span>
                  <span>{r.job_count} run{r.job_count !== 1 ? "s" : ""}</span>
                  <span className="text-stone-600 truncate">last: {r.last_video_label}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {r.needs_render && r.last_video_id && (
                  <button
                    onClick={() => navigate(`/studio/${r.last_video_id}`)}
                    className="btn-primary text-xs inline-flex items-center gap-1.5"
                    data-testid={`tracked-open-studio-${r.name}`}
                  >
                    Open in Studio <ArrowRight size={12} />
                  </button>
                )}
                {(r.clips_rendered || 0) > 0 && (
                  <button
                    onClick={() => navigate(`/rider/${encodeURIComponent(r.name)}`)}
                    className="btn-secondary text-xs inline-flex items-center gap-1.5"
                    data-testid={`tracked-open-profile-${r.name}`}
                  >
                    <Film size={12} /> View Profile
                  </button>
                )}
                {!r.needs_render && (r.clips_rendered || 0) === 0 && r.last_video_id && (
                  <button
                    onClick={() => navigate(`/studio/${r.last_video_id}`)}
                    className="btn-secondary text-xs inline-flex items-center gap-1.5"
                    data-testid={`tracked-retry-${r.name}`}
                  >
                    Retry in Studio <ArrowRight size={12} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  const color =
    accent === "amber" ? "text-amber-400"
    : accent === "emerald" ? "text-emerald-400"
    : "text-orange-500";
  return (
    <div className="border border-stone-800 p-3" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`font-display text-3xl font-bold ${color}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-1">{label}</div>
    </div>
  );
}
